import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { detalleRetencion, formatMoneda } from "./utils";

const CampoCert = ({ label, valor }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500}>
      {valor ?? "—"}
    </Typography>
  </Box>
);

const CertificadoRetencionCard = ({
  certificado,
  onDescargarPdf,
  descargandoPdf = false,
}) => {
  const {
    titulo,
    tipoDocumento,
    provincia,
    certificado: nombreCert,
    aplica,
    mensaje,
    base,
    importe,
    alicuota,
    condicion,
    codigoPadron,
    nroCertificado,
    idRetencion,
    pdfEnviadoPorMail = false,
  } = certificado;

  const sinRetencion = !aplica || (Number(importe) === 0 && mensaje);
  const puedeDescargarPdf =
    Boolean(onDescargarPdf) && Boolean(idRetencion) && !sinRetencion;

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        backgroundColor: "#fff",
        border: "1px solid #e8e8e8",
        borderLeft: "4px solid",
        borderLeftColor: sinRetencion ? "#bdbdbd" : "#2471A3",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          mb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DescriptionOutlinedIcon color="primary" fontSize="small" />
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              {titulo}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Documento independiente de la orden de pago
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {sinRetencion ? (
            <Chip label="No aplica" size="small" />
          ) : (
            <Chip label="Retención calculada" color="primary" size="small" variant="outlined" />
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<PictureAsPdfOutlinedIcon />}
            disabled={!puedeDescargarPdf || descargandoPdf}
            onClick={() => onDescargarPdf?.(idRetencion)}
            title={
              puedeDescargarPdf
                ? "Descargar certificado PDF"
                : pdfEnviadoPorMail
                  ? "PDF disponible tras generar la orden"
                  : "Disponible al confirmar la orden"
            }
          >
            PDF
          </Button>
        </Box>
      </Box>

      {sinRetencion && mensaje ? (
        <Typography variant="body2" color="text.secondary">
          {mensaje}
        </Typography>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4}>
              <CampoCert label="Tipo" valor={tipoDocumento} />
            </Grid>
            {provincia && (
              <Grid item xs={6} sm={4}>
                <CampoCert label="Provincia" valor={provincia} />
              </Grid>
            )}
            <Grid item xs={12} sm={provincia ? 4 : 8}>
              <CampoCert label="Certificado / concepto" valor={nombreCert} />
            </Grid>
            {nroCertificado ? (
              <Grid item xs={6} sm={4}>
                <CampoCert label="N° certificado" valor={nroCertificado} />
              </Grid>
            ) : null}
            {condicion && (
              <Grid item xs={12} sm={6}>
                <CampoCert
                  label="Condición ganancias"
                  valor={
                    codigoPadron
                      ? `${condicion} (${codigoPadron})`
                      : condicion
                  }
                />
              </Grid>
            )}
            <Grid item xs={6} sm={4}>
              <CampoCert label="Base imponible" valor={formatMoneda(base)} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <CampoCert
                label="Alícuota"
                valor={alicuota != null ? `${alicuota}%` : "—"}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <CampoCert
                label="Importe retenido"
                valor={formatMoneda(importe)}
              />
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            {detalleRetencion({ base, importe, alicuota, alicuota_efectiva: alicuota })}
          </Typography>
        </>
      )}
      <Divider sx={{ mt: 2 }} />
    </Paper>
  );
};

export default CertificadoRetencionCard;
